import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { memo } from 'react';
import Navigation from '@/components/theme-layouts/components/navigation/Navigation';
import NavbarPinToggleButton from '@/components/theme-layouts/components/navbar/NavbarPinToggleButton';
import Logo from '../../../../components/Logo';
import UserMenu from '@/components/theme-layouts/components/UserMenu';

import { useAppSelector } from '@/store/hooks';
import { selectFuseNavbar } from '@/components/theme-layouts/components/navbar/navbarSlice';
import useFuseLayoutSettings from '@fuse/core/FuseLayout/useFuseLayoutSettings';

const Root = styled('div')(({ theme }) => ({
	backgroundColor: 'var(--neutral-bg-light-colored)',
	color: theme.vars?.palette.text.primary ?? theme.palette.text.primary,
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	'& ::-webkit-scrollbar-thumb': {
		boxShadow: `inset 0 0 0 20px ${'rgba(255, 255, 255, 0.24)'}`,
		...theme.applyStyles('light', {
			boxShadow: `inset 0 0 0 20px ${'rgba(0, 0, 0, 0.24)'}`,
		}),
	},
	'& ::-webkit-scrollbar-thumb:active': {
		boxShadow: `inset 0 0 0 20px ${'rgba(255, 255, 255, 0.37)'}`,
		...theme.applyStyles('light', {
			boxShadow: `inset 0 0 0 20px ${'rgba(0, 0, 0, 0.37)'}`,
		}),
	},
}));

const StyledContent = styled('div')(() => ({
	flex: 1,
	overflowY: 'auto',
	overflowX: 'hidden',
	display: 'flex',
	flexDirection: 'column',
}));

type NavbarStyle2ContentProps = {
	className?: string;
};

/**
 * The navbar style 2 content.
 */
function NavbarStyle2Content(props: NavbarStyle2ContentProps) {
	const { className = '' } = props;

	const navbar = useAppSelector(selectFuseNavbar);
	const settings = useFuseLayoutSettings();
	const folded = settings.config?.navbar?.folded;
	const foldedandclosed = folded && !navbar.foldedOpen;

	return (
		<Root className={clsx(className)}>
			<div
				className={clsx('flex h-18 shrink-0 flex-row items-center', {
					'justify-center px-6': foldedandclosed,
					'justify-between px-6 gap-1.5': !foldedandclosed,
				})}
			>
				{!foldedandclosed && <Logo />}
				<NavbarPinToggleButton className="h-8 w-8 p-0 shrink-0" />
			</div>

			<StyledContent>
				<Navigation layout="vertical" />

				<div
					className={clsx('w-full p-4', {
						'flex justify-center': foldedandclosed,
					})}
				>
					<UserMenu foldedandclosed={foldedandclosed} />
				</div>
			</StyledContent>
		</Root>
	);
}

export default memo(NavbarStyle2Content);
